import { useEffect, useState } from 'react';

import { deleteCookie, setCookie } from 'cookies-next';
import { v4 as uuidv4 } from 'uuid';

import { getOS } from 'utils/getOS';

import { EventNames } from './events';
import {
  type TRequiredEventProperties,
  EventAction,
  EventObjectType,
} from './types';

const GENIUS_AI_SOURCE_KEY = 'geniusAiSource';
const ONE_DAY_IN_MS = 86400;

const defaultProperties: TRequiredEventProperties = {
  platform_name: ['iOS', 'Android'].includes(getOS())
    ? 'mobile_web'
    : 'desktop_web',
  product_area: 'booking_site',
  clientToken: '',
  businessId: '',
  theme_name: '',
};

export const trackEvent = <T extends Record<string, unknown>>(
  eventName: string,
  data?: T,
) => {
  const eventData = { ...defaultProperties, ...data };

  if (window.analytics) {
    if (__DEV__) {
      // eslint-disable-next-line no-console
      console.log('trackSegmentEvent:', eventName, eventData);
    }

    window.analytics.track(eventName, eventData);
  }
};

export const trackPageView = <T extends Record<string, unknown>>(
  pageName: string,
  data?: T,
) => {
  const eventData = { ...defaultProperties, ...data };

  if (window.analytics) {
    if (__DEV__) {
      // eslint-disable-next-line no-console
      console.info(`[Analytics] Page View:`, pageName, eventData);
    }

    window.analytics.page(pageName, eventData);
  }
};

export const useMetrics = (
  ownerId: string,
  businessId: string,
  themeName: string | undefined,
  referrer: string | undefined,
) => {
  const [trackedInit, trackInit] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const geniusAiSourceValue = urlParams.get('g') === 't';

    if (geniusAiSourceValue) {
      setCookie(GENIUS_AI_SOURCE_KEY, 'true', { maxAge: ONE_DAY_IN_MS });
    } else {
      deleteCookie(GENIUS_AI_SOURCE_KEY);
    }

    if (
      typeof window !== 'undefined' &&
      window.analytics &&
      !trackedInit &&
      ownerId
    ) {
      // set clientToken once
      const clientToken: string = uuidv4().toString();

      defaultProperties.clientToken = `${businessId}-${clientToken}`;
      defaultProperties.businessId = businessId;
      defaultProperties.business_owner_user_guid = ownerId;
      defaultProperties.theme_name = themeName;
      defaultProperties.referrer = referrer;
      // groups all visitors (clients booking appointments) under this salon - will allows us to take advantage of mixpanel grouping features in the future
      window.analytics.group(ownerId);
      window.analytics.identify(ownerId);

      // track event to load the init client side page render before we could mount a listener
      trackEvent(EventNames.SESSION_STARTED, {
        action: EventAction.VIEW,
        productSection: 'Booking',
        objectType: EventObjectType.PAGE,
        productSubSection: 'Landing Page',
      });

      trackInit(true);
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedInit, ownerId]);
};
